import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfCtaText, sfCtaUrl, sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const HeroGradient: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const gradientFrom = c.gradient_from || 'var(--brand-primary, #1a365d)';
  const gradientTo = c.gradient_to || 'var(--brand-secondary, #7c3aed)';
  const gradientDir = c.gradient_direction || '135deg';

  const keyframes = `
    @keyframes hg-float1 {
      0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
      50% { transform: translateY(-30px) rotate(15deg); opacity: 0.9; }
    }
    @keyframes hg-float2 {
      0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.4; }
      50% { transform: translateY(20px) rotate(-10deg); opacity: 0.7; }
    }
    @keyframes hg-float3 {
      0%, 100% { transform: translate(0px, 0px) scale(1); opacity: 0.3; }
      33% { transform: translate(15px, -20px) scale(1.05); opacity: 0.5; }
      66% { transform: translate(-10px, 10px) scale(0.97); opacity: 0.4; }
    }
  `;

  return (
    <header
      className="sb-render sb-render-hero-gradient"
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '120px 24px',
        background: `linear-gradient(${gradientDir}, ${gradientFrom} 0%, ${gradientTo} 100%)`,
        color: '#fff',
        textAlign: 'center',
      }}
    >
      <style>{keyframes}</style>

      {/* Animated floating shapes */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: '10%', left: '8%',
          width: 200, height: 200, borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          background: 'rgba(255,255,255,0.08)',
          animation: 'hg-float1 7s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', top: '20%', right: '10%',
          width: 140, height: 140, borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
          background: 'rgba(255,255,255,0.06)',
          animation: 'hg-float2 9s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', left: '20%',
          width: 100, height: 100, borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          animation: 'hg-float3 11s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '25%', right: '15%',
          width: 240, height: 240, borderRadius: '50% 50% 30% 70% / 50% 70% 30% 50%',
          background: 'rgba(255,255,255,0.04)',
          animation: 'hg-float1 13s ease-in-out infinite reverse',
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto' }}>
        <InlineEditText
          tag="h1"
          controlKey="headline"
          style={{ fontSize: '3.75rem', lineHeight: 1.1, marginBottom: 20, color: '#fff', fontWeight: 800, letterSpacing: '-0.025em' }}
        >
          {sfHeadline(c, 'Beautiful Gradient Hero')}
        </InlineEditText>
        <InlineEditText
          tag="p"
          controlKey="subheadline"
          style={{ fontSize: '1.25rem', opacity: 0.88, marginBottom: 40, maxWidth: 560, margin: '0 auto 40px' }}
        >
          {sfSub(c, 'Your compelling subheadline goes here')}
        </InlineEditText>
        <a
          href={sfCtaUrl(c)}
          className="sb-cta-btn"
          style={{
            display: 'inline-block',
            padding: '16px 40px',
            borderRadius: 'var(--brand-btn-radius, 6px)',
            background: '#fff',
            color: gradientFrom.startsWith('var') ? 'var(--brand-primary, #1a365d)' : gradientFrom,
            fontWeight: 700,
            textDecoration: 'none',
            fontSize: '1rem',
            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
        >
          <InlineEditText tag="span" controlKey="cta_text">
            {sfCtaText(c, 'Get Started')}
          </InlineEditText>
        </a>
      </div>
    </header>
  );
};

export default HeroGradient;
